import {
  Theme,
  color,
  propertyColorStyles,
  radius,
  rem,
  token,
  transition,
} from '@thiememeulenhoff/lit-sdk';
import { PropertyColor } from '@thiememeulenhoff/types';
import { css, unsafeCSS } from 'lit';
import { tagName } from '../../helpers';

export const tag = tagName('button');
export const theme = new Theme(tag);

const propertyColorCallback = (
  propertyColor: PropertyColor,
  propertyAltColor: PropertyColor,
) => {
  const selector = unsafeCSS(`[color=${propertyColor}]`);
  const subtleSelector = unsafeCSS(`${selector}[variant='subtle']`);

  // postcss-lit-disable-next-line
  return css`
    :host(${selector}) :where(.button:not([disabled])) {
      ${theme.props([
        [
          'background',
          `${propertyColor}-background`,
          color(propertyColor, 600),
        ],
        ['color', `${propertyColor}-color`, color(propertyAltColor)],
      ])};

      &:hover {
        ${theme.props([
          [
            'background',
            `${propertyColor}-background-hover`,
            color(propertyColor, 700),
          ],
        ])};
      }
    }

    :host(${selector}[active]:not([disabled])) :where(.button),
    :host(${selector}:not([disabled])) .button:is([active], :active) {
      ${theme.props([
        [
          'background',
          `${propertyColor}-background-active`,
          color(propertyColor, 800),
        ],
      ])};
    }

    :host(${selector}) :where(.button):focus-visible {
      outline-color: var(--tm-focus-ring-color);
      outline-offset: var(--tm-focus-ring-offset);
      outline-style: var(--tm-focus-ring-style);
      outline-width: var(--tm-focus-ring-width);
    }

    :host(${selector}[variant='ghost']) .button:not(:is(:hover, :active, [active], [disabled])) {
      background: transparent;
      color: ${color(propertyColor, 600)};
    }

    :host(${selector}[variant='subtle']) .button:not(:is(:hover, :active, [active], [disabled])) {
      background: ${color(propertyColor, 100)};
      color: ${color(propertyColor, 600)};
    }

    :host(${subtleSelector}) .button:hover:not([disabled]) {
      background: ${color(propertyColor, 200)};
      color: ${color(propertyColor, 700)};
    }

    :host(${selector}:not([disabled])[active][variant='ghost']) .button {
      background: ${color(propertyColor, 700)};
      color: ${color('white')};
    }

    :host(${selector}[variant='text']) .button {
      background: transparent;
      padding: 0;
    }

    :host(${selector}[variant='text']) .button:not(:is(:hover, :active, [active], [disabled])) {
      color: ${color(propertyColor, 600)};
    }

    :host(${selector}[variant='text']) .button:hover {
      color: ${color(propertyColor, 700)};
    }

    :host(${selector}[variant='text']) .button:is(:disabled, [disabled]) {
      color: ${color('grey', 500)};
    }

    :host(${selector}[variant='text']:not([disabled])) .button:is(:active, [active]),
    :host(${selector}[variant='text']:not([disabled]):is([active], :active)) .button {
      color: ${color(propertyColor, 800)};
    }

    :host(${subtleSelector}[active]) .button {
      background: ${color(propertyColor, 700)};
      color: ${color('white')};
    }

    :host(${subtleSelector}) .button:active:not([disabled]) {
      background: ${color(propertyColor, 700)};
      color: ${color('white')};
    }

    :host(${selector}) :where(.button[disabled]) {
      background: ${color('grey', 300)};
      color: ${color('grey', 400)};
      cursor: not-allowed;
    }
  `;
};

const styles = [];

styles.push(css`
  :host {
    display: inline-block;
  }

  :where(.button) {
    align-items: center;
    box-sizing: border-box;
    cursor: pointer;
    display: inline-flex;
    user-select: none;

    ${theme.props([
      ['block-size', 'block-size', rem(40)],
      ['inline-size', 'inline-size', '100%'],
      ['padding', 'padding', rem(0, 16)],
      ['border', 'border', '0'],
      ['border-radius', 'border-radius', radius('large')],
      ['color', 'color', color('grey', 600)],
      ['font-size', 'font-size', rem(15)],
      ['font-family', 'font-family', 'inherit'],
      ['font-weight', 'font-weight', '600'],
      ['gap', 'gap', rem(8)],
      ['line-height', 'line-height', '1'],
      ['text-align', 'text-align', 'center'],
      ['text-decoration', 'text-decoration', 'none'],
      [
        'transition',
        'transition',
        'color var(--tm-transition-duration-fast) var(--tm-transition-function-ease-out), background var(--tm-transition-duration-fast) var(--tm-transition-function-ease-out)',
      ],
      ['white-space', 'white-space', 'nowrap'],
    ])};
  }

  :host([size='small']) :where(.button) {
    ${theme.props([
      ['padding', 'padding', rem(6, 12)],
      ['font-size', 'font-size', rem(14)],
      ['block-size', 'block-size', rem(32)],
    ])};
  }

  :host([pill]) :where(.button) {
    border-radius: ${radius('extra-large')};
  }

  :where(.button:not([disabled])) {
    transition: ${transition('background-color', 'normal', 'ease-out')};
  }

  :where(.button:not([disabled])):hover {
    transition: ${transition('background-color', 'normal', 'ease-in')};
  }

  :host([animation='false']) :where(.button:not([disabled])),
  :host([animation='false']) :where(.button:not([disabled])):hover {
    transition: none;
  }

  :host(:where([variant='default'], [variant='subtle'])) :where(.button) {
    ${theme.props([
      ['box-shadow', 'box-shadow', token('elevation-shadow-raised')],
    ])};
  }
`);

styles.push(propertyColorStyles(propertyColorCallback));

export const buttonStyles = styles;
